import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BOOK_SAMPLES } from "@/data/bookSamples";

// Renders inline tags (<b>, <i>, <sup>) that live in the transcribed data.
function Html({ as: Tag = "span", html, ...rest }) {
  return <Tag {...rest} dangerouslySetInnerHTML={{ __html: html }} />;
}

function SampleBlock({ block }) {
  switch (block.type) {
    case "drop":
      return <Html as="p" className="ptoc__drop" html={block.text} />;
    case "h4":
      return <Html as="h4" html={block.text} />;
    case "ul":
      return (
        <ul>
          {block.items.map((item, i) => (
            <Html as="li" key={i} html={item} />
          ))}
        </ul>
      );
    case "quote":
      return <Html as="p" className="ptoc__quote" html={block.text} />;
    case "box":
      return (
        <div className="ptoc__box">
          {block.kicker ? <span className="ptoc__kicker">{block.kicker}</span> : null}
          {(block.blocks || []).map((b, i) => (
            <SampleBlock block={b} key={i} />
          ))}
          {block.ol ? (
            <ol>
              {block.ol.map((item, i) => (
                <Html as="li" key={i} html={item} />
              ))}
            </ol>
          ) : null}
        </div>
      );
    case "principle":
      return (
        <div className="ptoc__quote ptoc__principle">
          <span className="ptoc__kicker">Quantum Principle · {block.no}</span>
          <b>{block.name}</b>
          <br />
          <span className="ptoc__principle-text">{block.text}</span>
        </div>
      );
    case "p":
    default: {
      const style = {};
      if (block.align) style.textAlign = block.align;
      if (block.italic) style.fontStyle = "italic";
      return <Html as="p" style={style} html={block.text} />;
    }
  }
}

// Text free-sample reader: tabbed chapter panels (books).
function TextSample({ book, bookId, sampleRef, controller, cta }) {
  const tabs = book.tabs || [];
  const [active, setActive] = useState(tabs[0]?.id);
  const pageRef = useRef(null);

  // Let a TOC "Read sample" pill open a specific chapter tab.
  if (controller) controller.setActive = setActive;

  useEffect(() => {
    if (pageRef.current) pageRef.current.scrollTop = 0;
  }, [active]);

  function onTabKeyDown(e, index) {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const dir = e.key === "ArrowRight" ? 1 : tabs.length - 1;
    setActive(tabs[(index + dir) % tabs.length].id);
  }

  return (
    <div className="ptoc__sample" ref={sampleRef}>
      <header className="ptoc__head">
        <span className="ptoc__eyebrow">Free Sample</span>
        <h2 className="ptoc__title">Read the Opening Pages</h2>
        <p className="ptoc__sub">Start reading now, exactly as it appears in the book.</p>
      </header>

      <div className="ptoc__tabs" role="tablist" aria-label="Sample chapters">
        {tabs.map((tab, i) => {
          const selected = active === tab.id;
          return (
            <button
              key={tab.id}
              className="ptoc__tab"
              role="tab"
              id={`tab-${bookId}-${tab.id}`}
              aria-controls={`panel-${bookId}-${tab.id}`}
              aria-selected={selected}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActive(tab.id)}
              onKeyDown={(e) => onTabKeyDown(e, i)}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      <div className="ptoc__page" ref={pageRef} tabIndex={0} aria-label="Sample text, scrollable">
        {tabs.map((tab) => {
          const panel = book.panels[tab.id];
          return (
            <article
              key={tab.id}
              className="ptoc__panel"
              id={`panel-${bookId}-${tab.id}`}
              role="tabpanel"
              aria-labelledby={`tab-${bookId}-${tab.id}`}
              hidden={active !== tab.id}
            >
              {panel.kicker ? <span className="ptoc__kicker">{panel.kicker}</span> : null}
              <h3>{panel.heading}</h3>
              {panel.subheading ? <h4 className="ptoc__subhead">{panel.subheading}</h4> : null}
              {panel.blocks.map((block, bi) => (
                <SampleBlock block={block} key={bi} />
              ))}
              <div className="ptoc__end">
                <p>{panel.end.note}</p>
                <Link className="ptoc__cta" to={cta.to}>{cta.label}</Link>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

// Image page-preview carousel (journals). `controller` exposes goTo for TOC pills.
function PagesSample({ sample, sampleRef, controller, cta }) {
  const pages = sample.pages || [];
  const [index, setIndex] = useState(0);
  const trackRef = useRef(null);

  const go = (n) => {
    const i = Math.max(0, Math.min(pages.length - 1, n));
    setIndex(i);
    const track = trackRef.current;
    if (track && track.children[i]) {
      track.scrollTo({ left: track.children[i].offsetLeft - track.offsetLeft, behavior: "smooth" });
    }
  };

  // Let the parent trigger navigation from "Preview" pills.
  if (controller) controller.goTo = go;

  function onKeyDown(e) {
    if (e.key === "ArrowRight") { e.preventDefault(); go(index + 1); }
    if (e.key === "ArrowLeft") { e.preventDefault(); go(index - 1); }
  }

  function onScroll() {
    const track = trackRef.current;
    if (!track) return;
    const i = Math.round(track.scrollLeft / track.clientWidth);
    if (i !== index) setIndex(i);
  }

  return (
    <div className="ptoc__sample" ref={sampleRef}>
      <header className="ptoc__head">
        <span className="ptoc__eyebrow">Look Inside</span>
        <h2 className="ptoc__title">Preview the Pages</h2>
        <p className="ptoc__sub">{sample.sub}</p>
      </header>

      <div className="pv">
        <div
          className="pv__track"
          ref={trackRef}
          tabIndex={0}
          aria-label="Page preview, use arrow keys to move between pages"
          onKeyDown={onKeyDown}
          onScroll={onScroll}
        >
          {pages.map((page, i) => (
            <figure className="pv__slide" key={i}>
              <img
                src={page.img}
                alt={page.alt}
                loading="lazy"
                width={935}
                height={1210}
                onContextMenu={(e) => e.preventDefault()}
              />
              <Html as="figcaption" html={page.caption} />
            </figure>
          ))}
        </div>

        <div className="pv__nav">
          <button className="pv__btn" type="button" aria-label="Previous page" disabled={index === 0} onClick={() => go(index - 1)}>
            ←
          </button>
          <span className="pv__count" aria-live="polite">{index + 1} / {pages.length}</span>
          <button className="pv__btn" type="button" aria-label="Next page" disabled={index === pages.length - 1} onClick={() => go(index + 1)}>
            →
          </button>
        </div>

        <div className="pv__end">
          <p>Included in The Identity Shift Collection, with all three books and the other journals.</p>
          <Link className="ptoc__cta" to={cta.to}>{cta.label}</Link>
        </div>
      </div>
    </div>
  );
}

function BookLookInside({ bookId, onClose }) {
  const book = BOOK_SAMPLES[bookId];
  const sampleRef = useRef(null);
  // Lets a TOC pill drive the carousel (pages) or chapter tab (text).
  const pagesController = useRef({ goTo: null }).current;
  const textController = useRef({ setActive: null }).current;

  const hasTextSample = (book.tabs || []).length > 0 && !!book.panels;
  const hasPagesSample = book.sample && book.sample.type === "pages";
  const cta = book.cta || { label: "Get the Complete Collection ($47)", to: "/checkout" };

  function scrollToSample() {
    if (sampleRef.current) sampleRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function onPreviewPage(slide) {
    scrollToSample();
    if (pagesController.goTo) pagesController.goTo(slide);
  }

  function onReadSample(sampleId) {
    scrollToSample();
    if (textController.setActive) textController.setActive(sampleId);
  }

  return (
    <section className="ptoc" aria-labelledby={`ptoc-title-${bookId}`}>
      <div className="ptoc__wrap">
        {/* ================= CONTENTS ================= */}
        <header className="ptoc__head">
          <span className="ptoc__eyebrow">{book.eyebrow || "Inside the Book"}</span>
          <h2 className="ptoc__title" id={`ptoc-title-${bookId}`}>{book.title}</h2>
          <Html as="p" className="ptoc__sub" html={book.subtitle} />
        </header>

        {book.steps ? (
          <ul className="ptoc__steps" aria-label="What's included">
            {book.steps.map((step, i) => (
              <li key={i}>{step.name}{step.time ? <span>{step.time}</span> : null}</li>
            ))}
          </ul>
        ) : null}

        {book.toc.map((group, gi) => {
          const ListTag = group.ordered ? "ol" : "ul";
          const listProps = group.ordered && group.start ? { start: group.start } : {};
          return (
            <details className="ptoc__card" key={gi} open={group.open || undefined}>
              <summary className="ptoc__summary">
                <span>
                  <span className="ptoc__label">{group.label}</span>
                  <Html as="span" className="ptoc__card-title" html={group.title} />
                </span>
                <span className="ptoc__arrow" aria-hidden="true" />
              </summary>
              <ListTag className="ptoc__list" {...listProps}>
                {group.rows.map((row, ri) => (
                  <li className="ptoc__row" key={ri}>
                    <Html as="span" className="ptoc__name" html={row.name} />
                    {hasPagesSample && row.preview != null ? (
                      <button className="ptoc__pill" type="button" onClick={() => onPreviewPage(row.preview)}>
                        Preview
                      </button>
                    ) : row.sample ? (
                      <button className="ptoc__pill" type="button" onClick={() => onReadSample(row.sample)}>
                        Read sample
                      </button>
                    ) : null}
                  </li>
                ))}
              </ListTag>
            </details>
          );
        })}

        {/* ================= SAMPLE ================= */}
        {hasPagesSample ? (
          <PagesSample sample={book.sample} sampleRef={sampleRef} controller={pagesController} cta={cta} />
        ) : hasTextSample ? (
          <TextSample book={book} bookId={bookId} sampleRef={sampleRef} controller={textController} cta={cta} />
        ) : null}

        {book.note ? <Html as="p" className="ptoc__note" html={book.note} /> : null}

        {onClose ? (
          <div className="ptoc__back">
            <button type="button" className="ptoc__back-btn" onClick={onClose}>
              ← Back to books
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export default function BookLookInsideModal({ bookId, onClose }) {
  const dialogRef = useRef(null);

  // Lock body scroll, close on Escape, and focus the dialog on open.
  useEffect(() => {
    document.body.classList.add("ptoc-modal-open");
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    if (dialogRef.current) dialogRef.current.focus();
    return () => {
      document.body.classList.remove("ptoc-modal-open");
      document.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  if (!bookId || !BOOK_SAMPLES[bookId]) return null;

  return (
    <div className="ptoc-modal__overlay" onClick={onClose} role="presentation">
      <div
        className="ptoc-modal__dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Look inside ${BOOK_SAMPLES[bookId].title}`}
        tabIndex={-1}
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <button type="button" className="ptoc-modal__close" aria-label="Close" onClick={onClose}>
          ×
        </button>
        <BookLookInside bookId={bookId} onClose={onClose} />
      </div>
    </div>
  );
}
